import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = ["film"]
  static values = {
    returnTo: String,
    pushed: Boolean,
    prevUrl: String,
    nextUrl: String
  }

  connect() {
    this.inertRoots = ["header", "main", "footer"].map((selector) => document.querySelector(selector)).filter(Boolean)
    this.lockPage()

    this.onKeydown = (event) => this.keydown(event)
    // Turbo clones the page on the next turn after this event. The clone must
    // not include the scroll lock, and the open viewer must get the lock back
    // on the turn after that clone.
    this.beforeCache = () => {
      this.releasePage()
      setTimeout(() => {
        setTimeout(() => {
          if (this.closed || !this.element.isConnected) return
          this.lockPage()
        }, 0)
      }, 0)
    }
    document.addEventListener("keydown", this.onKeydown)
    document.addEventListener("turbo:before-cache", this.beforeCache)
    this.scrollActiveThumb()
    this.element.querySelector("[data-close]")?.focus()
  }

  disconnect() {
    this.closed = true
    this.releasePage()
    document.removeEventListener("keydown", this.onKeydown)
    document.removeEventListener("turbo:before-cache", this.beforeCache)
  }

  close(event) {
    event.preventDefault()
    const frame = document.getElementById("photo_modal")
    frame?.replaceChildren()

    const returnTo = this.returnToValue || "/"
    if (this.pushedValue) {
      const clearRestoredModal = () => document.getElementById("photo_modal")?.replaceChildren()
      document.addEventListener("turbo:render", clearRestoredModal, { once: true })
      history.back()
      return
    }

    Turbo.visit(returnTo, { action: "replace" })
  }

  keydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close(event)
      return
    }

    const typing = event.target.closest("textarea, input, [contenteditable]")
    if (typing) {
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        event.preventDefault()
        event.target.closest("form")?.requestSubmit()
      }
      return
    }

    if (event.key === "ArrowLeft" && this.prevUrlValue) {
      event.preventDefault()
      this.visit(this.prevUrlValue)
    } else if (event.key === "ArrowRight" && this.nextUrlValue) {
      event.preventDefault()
      this.visit(this.nextUrlValue)
    }
  }

  pointerDown(event) {
    if (event.target.closest("a, button, textarea, input, form")) return
    this.swipeX = event.clientX
    this.swipeY = event.clientY
    this.swipeOnPhoto = !!event.target.closest("[data-photo]")
    if (this.swipeOnPhoto) event.target.addEventListener("dragstart", (drag) => drag.preventDefault(), { once: true })
  }

  pointerUp(event) {
    if (this.swipeX == null) return
    const dx = event.clientX - this.swipeX
    const dy = event.clientY - this.swipeY
    const onPhoto = this.swipeOnPhoto
    this.swipeX = null
    this.swipeY = null
    this.swipeOnPhoto = false

    if (Math.abs(dx) < 12 && Math.abs(dy) < 12 && onPhoto && this.mobileNav()) {
      const box = event.target.closest("[data-photo]")?.getBoundingClientRect()
      if (!box) return
      const url = event.clientX < box.left + box.width / 2 ? this.prevUrlValue : this.nextUrlValue
      if (url) this.visit(url)
      return
    }

    if (Math.abs(dx) < 56 || Math.abs(dx) < Math.abs(dy)) return
    const url = dx < 0 ? this.nextUrlValue : this.prevUrlValue
    if (url) this.visit(url)
  }

  mobileNav() {
    return window.matchMedia("(max-width: 767px)").matches
  }

  visit(url) {
    Turbo.visit(url, { frame: "photo_modal", action: "replace" })
  }

  scrollActiveThumb() {
    if (!this.hasFilmTarget) return
    const active = this.filmTarget.querySelector("[data-active-thumb]")
    if (!active) return
    this.filmTarget.scrollLeft = active.offsetLeft - this.filmTarget.clientWidth / 2 + active.clientWidth / 2
  }

  lockPage() {
    document.documentElement.classList.add("overflow-hidden")
    document.body.classList.add("overflow-hidden")
    this.inertRoots?.forEach((el) => { el.inert = true })
  }

  releasePage() {
    document.documentElement.classList.remove("overflow-hidden")
    document.body.classList.remove("overflow-hidden")
    this.inertRoots?.forEach((el) => { el.inert = false })
  }
}
